import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  Spinner,
  useIsMobile,
  useToast,
} from "../../design-system";
import { useTimeTracking } from "../../hooks/useTimeTracking";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { formatTimeHHMM } from "../../helpers/datetime";
import { useStartTour } from "../../tours/TourContext";
import NewRequestModal from "../../Components/Kronaly/Overtime/NewRequestModal";
import ShiftLimitModal from "../../Components/Kronaly/TimeTracking/ShiftLimitModal";
import { getNotifications, markNotificationRead } from "../../helpers/kronaly_helper";

const STATE_CONFIG = {
  off: {
    label: "Fuera de jornada",
    desc: "Aún no has marcado entrada hoy.",
    badgeTone: "neutral",
    action: { label: "Marcar entrada", icon: "Play", variant: "primary", kind: "clock-in" },
  },
  working: {
    label: "Trabajando",
    desc: "Tu jornada está activa.",
    badgeTone: "success",
    action: { label: "Marcar salida", icon: "Square", variant: "dangerSoft", kind: "clock-out" },
    secondary: { label: "Iniciar pausa", icon: "Coffee", variant: "secondary", kind: "break-start" },
  },
  paused: {
    label: "En pausa",
    desc: "Tu jornada está pausada. Reanuda cuando vuelvas.",
    badgeTone: "warning",
    action: { label: "Reanudar", icon: "Play", variant: "primary", kind: "break-end" },
    secondary: { label: "Marcar salida", icon: "Square", variant: "dangerSoft", kind: "clock-out" },
  },
  finished: {
    label: "Jornada finalizada",
    desc: "Has cerrado tu jornada de hoy.",
    badgeTone: "info",
    action: { label: "Marcar entrada de nuevo", icon: "Play", variant: "secondary", kind: "clock-in" },
    secondary: { label: "Ver historial", icon: "ArrowRight", variant: "ghost", kind: "history" },
  },
};

// Instante (clock_in/out) en la zona del workspace, formato "HH:MM".
function fmtTime(iso, tz) {
  return formatTimeHHMM(iso, tz);
}

// Cabecera con la fecha de HOY según la zona del workspace.
function fmtTodayHeader(tz) {
  try {
    const txt = new Intl.DateTimeFormat("es-ES", {
      timeZone: tz,
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    }).format(new Date());
    return txt.charAt(0).toUpperCase() + txt.slice(1);
  } catch {
    return "";
  }
}

function MiJornadaContent() {
  document.title = "Mi jornada | Kronaly";
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useIsMobile();
  const { status, error, jornadaState, seconds, breakSeconds, clockIn, clockOut, startBreak, endBreak } =
    useTimeTracking({ poll: true, tick: true });
  useStartTour("jornada");

  const [busy, setBusy] = useState(false);
  const [overtimeModalOpen, setOvertimeModalOpen] = useState(false);
  const [shiftWarning, setShiftWarning] = useState(null);
  const warningShownRef = useRef(false);

  const { employeeProfileId, timezone } = useCurrentMembership();

  useEffect(() => {
    if (jornadaState !== "working") return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await getNotifications();
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data ?? res;
        const items = Array.isArray(payload?.items) ? payload.items : [];

        // Solo nos interesan los avisos de turno del turno ACTIVO. Una
        // notificación de un turno anterior (p. ej. uno que quedó sin cerrar)
        // no debe dispararse al iniciar una jornada nueva.
        const activeEntryId = status?.active_time_entry_id ?? null;
        const SHIFT_TYPES = ["shift_limit_reached", "shift_approaching_limit", "shift_auto_closed"];
        const belongsToActiveShift = (n) =>
          activeEntryId != null && (n.data?.time_entry_id ?? null) === activeEntryId;

        // Auto-limpieza: marca como leídas las notificaciones de turno
        // obsoletas (sin leer y de otro turno) para que no reaparezcan.
        for (const n of items) {
          if (SHIFT_TYPES.includes(n.type) && !n.read_at && !belongsToActiveShift(n)) {
            markNotificationRead(n.id).catch(() => {});
          }
        }

        const limitNotif = items.find(
          (n) => n.type === "shift_limit_reached" && !n.read_at && belongsToActiveShift(n)
        );
        const preNotif = items.find(
          (n) => n.type === "shift_approaching_limit" && !n.read_at && belongsToActiveShift(n)
        );
        const autoClosedNotif = items.find(
          (n) => n.type === "shift_auto_closed" && !n.read_at && belongsToActiveShift(n)
        );
        if (autoClosedNotif && !warningShownRef.current) {
          warningShownRef.current = true;
          setShiftWarning(null);
          toast.warning("Turno cerrado automáticamente", autoClosedNotif.body);
        } else if (limitNotif && !warningShownRef.current) {
          warningShownRef.current = true;
          setShiftWarning({ type: "limit_reached", message: limitNotif.body, notifId: limitNotif.id });
        } else if (preNotif && !warningShownRef.current) {
          warningShownRef.current = true;
          toast.success("Jornada próxima a finalizar", preNotif.body);
        }
      } catch {}
    };

    poll();
    const id = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [jornadaState, toast, status?.active_time_entry_id]);

  const breakHh = String(Math.floor(breakSeconds / 3600)).padStart(2, "0");
  const breakMm = String(Math.floor((breakSeconds % 3600) / 60)).padStart(2, "0");
  const breakSs = String(breakSeconds % 60).padStart(2, "0");

  const cfg = STATE_CONFIG[jornadaState];

  const hh = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  // Horas esperadas reales del horario del día (devueltas por el backend).
  // Fallback a 8h si el workspace aún no devuelve el dato o no hay horario.
  const expectedMin = status?.expected_minutes > 0 ? status.expected_minutes : 480;
  const expected = expectedMin * 60;
  const expectedLabel = `${String(Math.floor(expectedMin / 60)).padStart(2, "0")}:${String(expectedMin % 60).padStart(2, "0")}`;
  const pct = Math.min(100, Math.round((seconds / expected) * 100));
  const remainingMin = Math.max(0, Math.ceil((expected - seconds) / 60));

  const entries = status?.entries || [];
  const firstEntry = entries[0];
  const lastEntry = entries[entries.length - 1];
  const entradaTxt = fmtTime(firstEntry?.clock_in_at, timezone);
  const salidaTxt = status?.is_active
    ? "En curso"
    : fmtTime(lastEntry?.clock_out_at, timezone);

  const triggerAction = async (kind) => {
    if (busy) return;
    if (kind === "history") {
      navigate("/jornada/historial");
      return;
    }
    setBusy(true);
    try {
      if (kind === "clock-in") {
        await clockIn();
        toast.success("Entrada marcada", `Registrada a las ${fmtTime(new Date().toISOString(), timezone)}`);
      } else if (kind === "clock-out") {
        await clockOut();
        toast.success("Salida marcada", `Registrada a las ${fmtTime(new Date().toISOString(), timezone)}`);
      } else if (kind === "break-start") {
        await startBreak();
        toast.success("Pausa iniciada", "Recuerda reanudar cuando vuelvas.");
      } else if (kind === "break-end") {
        await endBreak();
        toast.success("Pausa finalizada", "Continúa con tu jornada.");
      }
    } catch (e) {
      const msg = typeof e === "string" ? e : e?.message || "No se pudo completar la acción";
      toast.error("Acción no completada", msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kr-page">
      <div>
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "var(--kr-fg-muted)",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          {fmtTodayHeader(timezone)}
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "var(--kr-fg-strong)",
          }}
        >
          Mi jornada
        </h1>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        <Card style={{ padding: isMobile ? 18 : 28 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <Badge tone={cfg.badgeTone} dot>
                {cfg.label}
              </Badge>
              <div
                style={{
                  fontSize: 14,
                  color: "var(--kr-fg-muted)",
                  marginTop: 8,
                }}
              >
                {cfg.desc}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "var(--kr-fg-muted)",
                  fontWeight: 600,
                }}
              >
                Hora actual
              </div>
              <div
                style={{
                  fontFamily: "var(--kr-font-mono)",
                  fontVariantNumeric: "tabular-nums",
                  fontSize: 18,
                  color: "var(--kr-fg-strong)",
                  fontWeight: 600,
                }}
              >
                <LiveClock />
              </div>
            </div>
          </div>

          <div
            data-tour="jornada-counter"
            style={{
              marginTop: 24,
              display: "flex",
              alignItems: "baseline",
              gap: 12,
            }}
          >
            <div
              style={{
                fontFamily: "var(--kr-font-mono)",
                fontVariantNumeric: "tabular-nums",
                fontSize: isMobile ? 64 : 96,
                fontWeight: 600,
                letterSpacing: "-0.04em",
                color: "var(--kr-fg-strong)",
                lineHeight: 1,
              }}
            >
              {hh}
              <span style={{ color: "var(--kr-fg-subtle)" }}>:</span>
              {mm}
              <span style={{ fontSize: isMobile ? 28 : 40, color: "var(--kr-fg-muted)" }}>
                :{ss}
              </span>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 6,
            }}
          >
            <span style={{ fontSize: 13, color: "var(--kr-fg-muted)" }}>
              de {expectedLabel} h esperadas{" "}
              <span style={{ color: "var(--kr-fg-strong)", fontWeight: 600 }}>
                · faltan {Math.floor(remainingMin / 60)} h {remainingMin % 60} min
              </span>
            </span>
            <span
              style={{
                fontSize: 13,
                color: "var(--kr-fg-muted)",
                fontFamily: "var(--kr-font-mono)",
              }}
            >
              {pct} %
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: "var(--kr-neutral-100)",
              borderRadius: 999,
              overflow: "hidden",
              marginTop: 6,
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: "100%",
                background: "var(--kr-action)",
              }}
            />
          </div>

          {jornadaState === "paused" && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: "var(--kr-status-warning-bg)",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "var(--kr-warning-700)",
              }}
            >
              <Icon name="Coffee" size={16} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                En pausa · {breakHh}:{breakMm}:{breakSs}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
                Desde {fmtTime(status?.current_break?.start_at, timezone)}
              </span>
            </div>
          )}

          <div
            data-tour="jornada-actions"
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 10,
              marginTop: 24,
            }}
          >
            <Button
              size="lg"
              variant={cfg.action.variant}
              icon={cfg.action.icon}
              onClick={() => triggerAction(cfg.action.kind)}
              disabled={busy}
              style={{ flex: 1 }}
            >
              {busy ? "Procesando…" : cfg.action.label}
            </Button>
            {cfg.secondary && (
              <Button
                size="lg"
                variant={cfg.secondary.variant}
                icon={cfg.secondary.icon}
                onClick={() => triggerAction(cfg.secondary.kind)}
                disabled={busy}
                style={{ flex: 1 }}
              >
                {cfg.secondary.label}
              </Button>
            )}
            {jornadaState === "working" && (
              <Button
                size="lg"
                variant="soft"
                icon="Clock"
                onClick={() => setOvertimeModalOpen(true)}
                disabled={busy}
                style={{ flex: 1 }}
              >
                Solicitar horas extra
              </Button>
            )}
          </div>

          <div
            data-tour="jornada-minstats"
            style={{
              display: isMobile ? "grid" : "flex",
              gridTemplateColumns: isMobile ? "1fr 1fr" : undefined,
              gap: isMobile ? 12 : 18,
              marginTop: 18,
              paddingTop: 18,
              borderTop: "1px solid var(--kr-border-subtle)",
            }}
          >
            <MiniStat label="Entrada" value={entradaTxt} />
            <MiniStat label="Salida" value={salidaTxt} />
            <MiniStat label="Marcajes" value={String(entries.length)} />
            <MiniStat label="Trabajado neto" value={`${hh}:${mm} h`} />
          </div>
        </Card>

        <Card>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            Eventos del día
          </div>
          <JornadaTimeline
            entries={entries}
            breaks={status?.breaks || []}
            currentBreak={status?.current_break || null}
            isActive={!!status?.is_active}
            tz={timezone}
          />
        </Card>
      </div>

      <MiJornadaModals
        employeeProfileId={employeeProfileId}
        overtimeModalOpen={overtimeModalOpen}
        setOvertimeModalOpen={setOvertimeModalOpen}
        shiftWarning={shiftWarning}
        setShiftWarning={setShiftWarning}
      />
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{label}</div>
      <div
        style={{
          fontFamily: "var(--kr-font-mono)",
          fontVariantNumeric: "tabular-nums",
          fontSize: 16,
          fontWeight: 600,
          color: "var(--kr-fg-strong)",
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const h = String(now.getHours()).padStart(2, "0");
  const m = String(now.getMinutes()).padStart(2, "0");
  return (
    <span>
      {h}:{m}
    </span>
  );
}

function JornadaTimeline({ entries, breaks, currentBreak, isActive, tz }) {
  const events = useMemo(() => {
    const list = [];
    entries.forEach((e, idx) => {
      list.push({
        sortKey: e.clock_in_at || "",
        time: fmtTime(e.clock_in_at, tz),
        label: idx === 0 ? "Entrada marcada" : "Reanudación",
        tone: "success",
        icon: "Play",
      });
      if (e.clock_out_at) {
        const dur = e.duration_minutes != null ? `${e.duration_minutes} min` : null;
        list.push({
          sortKey: e.clock_out_at,
          time: fmtTime(e.clock_out_at, tz),
          label: e.auto_stopped_at ? "Cierre automático" : "Salida marcada",
          tone: e.auto_stopped_at ? "warning" : "neutral",
          icon: "Square",
          note: dur ? `Tramo · ${dur}` : null,
        });
      }
    });
    (breaks || []).forEach((b) => {
      if (!b.end_at) return;
      const startMs = new Date(b.start_at).getTime();
      const endMs = new Date(b.end_at).getTime();
      const mins = Math.max(0, Math.round((endMs - startMs) / 60000));
      list.push({
        sortKey: b.start_at,
        time: fmtTime(b.start_at, tz),
        label: "Inicio de pausa",
        tone: "warning",
        icon: "Coffee",
      });
      list.push({
        sortKey: b.end_at,
        time: fmtTime(b.end_at, tz),
        label: "Fin de pausa",
        tone: "neutral",
        icon: "Play",
        note: `Pausa · ${mins} min`,
      });
    });
    if (currentBreak?.start_at) {
      list.push({
        sortKey: currentBreak.start_at,
        time: fmtTime(currentBreak.start_at, tz),
        label: "Pausa en curso",
        tone: "warning",
        icon: "Coffee",
        live: true,
      });
    } else if (isActive) {
      list.push({
        sortKey: new Date().toISOString(),
        time: fmtTime(new Date().toISOString(), tz),
        label: "Marcaje en curso",
        tone: "info",
        icon: "Clock",
        live: true,
      });
    }
    list.sort((a, b) => (a.sortKey > b.sortKey ? 1 : -1));
    return list;
  }, [entries, breaks, currentBreak, isActive, tz]);

  const tones = {
    success: "var(--kr-success-500)",
    warning: "var(--kr-warning-500)",
    info: "var(--kr-info-500)",
    neutral: "var(--kr-neutral-400)",
  };

  if (events.length === 0) {
    return (
      <div style={{ fontSize: 13, color: "var(--kr-fg-muted)", padding: "8px 0" }}>
        Aún no hay marcajes hoy.
      </div>
    );
  }

  return (
    <div
      style={{ display: "flex", flexDirection: "column", position: "relative" }}
    >
      <span
        style={{
          position: "absolute",
          left: 13,
          top: 12,
          bottom: 12,
          width: 1,
          background: "var(--kr-border-subtle)",
        }}
      />
      {events.map((e, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 0",
            position: "relative",
          }}
        >
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 999,
              background: "#fff",
              border: `2px solid ${tones[e.tone]}`,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: tones[e.tone],
              zIndex: 1,
              boxShadow: e.live ? `0 0 0 3px ${tones[e.tone]}33` : "none",
            }}
          >
            <Icon name={e.icon} size={12} />
          </span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--kr-fg-strong)",
              }}
            >
              {e.label}
            </div>
            {e.note && (
              <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
                {e.note}
              </div>
            )}
          </div>
          <span
            style={{
              fontFamily: "var(--kr-font-mono)",
              fontSize: 12,
              color: "var(--kr-fg-muted)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {e.time}
          </span>
        </div>
      ))}
    </div>
  );
}

function MiJornadaModals({ employeeProfileId, overtimeModalOpen, setOvertimeModalOpen, shiftWarning, setShiftWarning }) {
  const toast = useToast();
  return (
    <>
      <NewRequestModal
        open={overtimeModalOpen}
        employeeProfileId={employeeProfileId}
        onClose={() => setOvertimeModalOpen(false)}
        onCreated={() => {
          toast.success("Solicitud enviada", "Tu manager la revisará en breve.");
        }}
      />
      <ShiftLimitModal
        open={!!shiftWarning && shiftWarning.type === "limit_reached"}
        warning={shiftWarning}
        employeeProfileId={employeeProfileId}
        onDismiss={() => {
          if (shiftWarning?.notifId) {
            markNotificationRead(shiftWarning.notifId).catch(() => {});
          }
          setShiftWarning(null);
        }}
      />
    </>
  );
}

function MiJornada() {
  const { employeeProfileId, loading, error } = useCurrentMembership();

  if (loading) {
    return (
      <div className="kr-page" style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="kr-page">
        <Alert tone="danger">{error}</Alert>
      </div>
    );
  }

  // Sin perfil de empleado en este workspace (caso owner/admin externo): no
  // hay jornada que mostrar. El backend devolvería NO_EMPLOYEE_PROFILE en
  // /time-tracking/status; lo evitamos en cliente con un estado vacío.
  if (!employeeProfileId) {
    return (
      <div className="kr-page">
        <EmptyState
          icon="Clock"
          title="No fichas en este workspace"
          description="Solo los empleados con perfil registrado pueden fichar y ver su jornada. Si necesitas fichar aquí, pide al responsable que cree tu perfil de empleado."
        />
      </div>
    );
  }

  return <MiJornadaContent />;
}

export default MiJornada;
